import React, { ReactNode, useEffect, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import Spinner, { SPINNER_SIZES, SPINNER_THEMES } from '../../icons/Spinner';
import { trackEvent } from '../../../utils/analytics';
import { getSubmitDisabledReason, serializeFormErrors } from './formDiagnostics';

export const pick = (obj: any, keys: string[]) => {
  return keys.reduce((acc, key) => {
    return { ...acc, [key]: obj[key] }
  }, {})
}
const hasDirtyField = (field: boolean | string | string[]): boolean => {
  if (typeof field === 'boolean') {
    return field
  }

  if (typeof field == 'string') {
    return true
  }

  return field.some((item) => {
    if (Array.isArray(item)) {
      return item.some((item) => hasDirtyField(item))
    }

    return item
  })
}

export const getDirtyFields = (dirtyFields: any): string[] => {
  return Object.keys(dirtyFields).reduce((accumulator: string[], key: string) => {
    const dirtyField = dirtyFields[key]

    if (Array.isArray(dirtyField)) {
      const hasDirty = dirtyField.map((item) => {
        if (typeof item === 'boolean') {
          return item
        }

        return getDirtyFields(item)
      
      }).some(hasDirtyField)
      return hasDirty ? [...accumulator, key] : accumulator
    }

    if (typeof dirtyField === 'object') {
      const hasDirty = getDirtyFields(dirtyField).some(hasDirtyField)
      return hasDirty ? [...accumulator, key] : accumulator
    }

    return dirtyField ? [...accumulator, key] : accumulator
  }, [])
}

// TODO: Use generic types
interface FormProps {
  className?: string,
  values?: { [x: string]: any },
  defaultValues?: { [x: string]: any },
  submitButtonText: string,
  children: ReactNode,
  readOnly?: boolean,
  onSubmit: (allValues: { [x: string]: any }, changedValues: { [x: string]: any }) => Promise<void>,
  onConfirmSubmit?: (allValues: { [x: string]: any }, changedValues: { [x: string]: any }, onContinueCallback: () => void) => void,
  isNewEntity?: boolean,
  loading?: boolean,
  // When provided, the form emits diagnostic Mixpanel events (why the submit
  // button is disabled, and what happens on a save attempt). Left undefined by
  // every other consumer, so those forms stay silent.
  logContext?: { [x: string]: any },
}

const Form = ({ className, values, defaultValues, submitButtonText, readOnly = false, isNewEntity = false, onSubmit, onConfirmSubmit, loading, logContext, children }: FormProps) => {
  const form = useForm({
    defaultValues,
    values,
    mode: "onChange",
  } as any);

  const {
    getValues,
    reset,
    formState: { isValid, isDirty, dirtyFields, errors }
  } = form

  // Log the disabled reason / validation errors whenever they change, but only
  // when they actually change — otherwise this would fire on every keystroke.
  const lastLoggedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!logContext) return

    const { reason, fields } = getSubmitDisabledReason(
      { isValid, isDirty, errors },
      { readOnly, loading, isNewEntity }
    )
    const serializedErrors = serializeFormErrors(errors)
    const signature = JSON.stringify({ reason, fields, serializedErrors })
    if (lastLoggedRef.current === signature) return
    lastLoggedRef.current = signature

    trackEvent('campaign_form_validation_state', {
      ...logContext,
      disabledReason: reason,
      invalidFields: fields,
      isValid,
      isDirty,
      errors: serializedErrors,
    })
  }, [logContext, isValid, isDirty, errors, readOnly, loading, isNewEntity])

  const handleSubmit = async () => {
    const allValues = getValues()
    const dirtyValues = pick(allValues, getDirtyFields(dirtyFields))

    if (logContext) {
      trackEvent('campaign_save_attempt', {
        ...logContext,
        isValid,
        isDirty,
        errors: serializeFormErrors(errors),
      })
    }

    const submit = async () => {
      await onSubmit(allValues, dirtyValues)
        .then(() => reset(allValues))
        .catch((errors: [string, {type: string; message: string}][]) => {
          if (Array.isArray(errors)) {
            errors.forEach(([name, error]) => form.setError(name, error))
          }
        })
    }

    if (onConfirmSubmit) {
      return onConfirmSubmit(allValues, dirtyValues, submit)
    }

    return submit()

  }

  return (
    <form className={className}>
      <FormProvider {...form}>
        {children}

        <div className="my-5 px-4 py-3 text-right sm:px-6">
          {!readOnly && <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:text-white disabled:cursor-not-allowed disabled:hover:bg-gray-300 disabled:opacity-100 disabled:shadow-none disabled:focus:outline-none disabled:focus:ring-0"
            disabled={!isValid || (!isNewEntity && !isDirty)}
          >
            {submitButtonText}
            {loading && <Spinner className="ml-3" size={SPINNER_SIZES.SMALL} theme={SPINNER_THEMES.DARK} />}
          </button>
          }
        </div>
      </FormProvider>
    </form>
  );
};

export default Form;



